import React, { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useOutletContext } from 'react-router-dom';
import { useData } from '../../context/DataContext';
import './Enrollments.css';

const StudyRegistrations = () => {
  const { t } = useTranslation();
  const { data, getCourseById } = useData();
  const { selectedLocation } = useOutletContext();
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedStudy, setSelectedStudy] = useState('');
  const [selectedYear, setSelectedYear] = useState('');
  const [selectedStatus, setSelectedStatus] = useState('');

  const studies = useMemo(() => data?.studies || [], [data?.studies]);
  const courses = useMemo(() => data?.courses || [], [data?.courses]);

  const getCohortDisplayName = (cohort) => {
    const name = String(cohort?.name || '').trim();
    const code = String(cohort?.code || '').trim();
    if (!name) return code;
    if (!code) return name;
    if (name.toLowerCase().includes(code.toLowerCase())) return name;
    return `${name} (${code})`;
  };

  const getStudyOptionLabel = (study) => {
    const cohortNames = (study.cohorts || []).map(getCohortDisplayName).filter(Boolean);
    return cohortNames.length > 0 ? `${study.name} ${cohortNames.join(', ')}` : study.name;
  };

  const courseMetadata = useMemo(() => {
    const entries = [];

    studies.forEach((study) => {
      const yearsById = new Map(
        (study.studyYears || []).map((year) => [String(year.id || year._id), year])
      );

      courses.forEach((course) => {
        const yearId = String(course.studyYear?.id || course.studyYear || '');
        if (!yearsById.has(yearId)) {
          return;
        }

        const year = yearsById.get(yearId);
        const linkedModules = Array.isArray(course.modules) && course.modules.length > 0
          ? course.modules
          : (course.module ? [course.module] : []);

        if (linkedModules.length === 0) {
          entries.push({
            id: String(course.id || course._id),
            studyId: String(study.id || study._id),
            studyName: study.name,
            studyLocation: study.location,
            yearId,
            yearLabel: year.name || `Studiejaar ${year.yearNumber || year.year}`,
            yearNumber: year.yearNumber || year.year,
            moduleId: `course-${String(course.id || course._id)}-default`,
            moduleName: 'Module nog niet ingesteld',
            courseName: course.name,
            courseCode: course.code,
            courseLocation: course.location
          });
          return;
        }

        linkedModules.forEach((moduleDoc) => {
          const moduleId = String(moduleDoc?.id || moduleDoc?._id || moduleDoc || '');
          entries.push({
            id: String(course.id || course._id),
            studyId: String(study.id || study._id),
            studyName: study.name,
            studyLocation: study.location,
            yearId,
            yearLabel: year.name || `Studiejaar ${year.yearNumber || year.year}`,
            yearNumber: year.yearNumber || year.year,
            moduleId,
            moduleName: moduleDoc?.name || moduleDoc?.code || 'Module',
            courseName: course.name,
            courseCode: course.code,
            courseLocation: course.location
          });
        });
      });
    });

    return entries;
  }, [courses, studies]);

  const courseMetadataById = useMemo(() => {
    const mapping = new Map();
    const ordered = [...courseMetadata].sort((a, b) => Number(a.yearNumber || 0) - Number(b.yearNumber || 0));
    ordered.forEach((entry) => {
      if (!mapping.has(entry.id)) {
        mapping.set(entry.id, entry);
      }
    });
    return mapping;
  }, [courseMetadata]);

  const studiesWithAssignments = useMemo(() => {
    const studyIds = new Set(courseMetadata.map((entry) => String(entry.studyId)));
    return studies.filter((study) => studyIds.has(String(study.id || study._id)));
  }, [courseMetadata, studies]);

  const groupedRegistrations = useMemo(() => {
    const registrations = new Map();

    data.enrollments.forEach((enrollment) => {
      const metadata = courseMetadataById.get(String(enrollment.courseId));
      const course = getCourseById(enrollment.courseId);
      const student = data.students.find((item) => String(item.id) === String(enrollment.studentId));

      if (!metadata || !student) {
        return;
      }

      const normalizedLocation = String(selectedLocation || 'all').toLowerCase();
      const enrollmentLocation = String(course?.location || metadata.courseLocation || student.location || '').toLowerCase();
      if (normalizedLocation !== 'all' && enrollmentLocation !== normalizedLocation) {
        return;
      }

      if (selectedStudy && metadata.studyId !== selectedStudy) {
        return;
      }

      if (selectedYear && metadata.yearId !== selectedYear) {
        return;
      }

      if (selectedStatus && enrollment.status !== selectedStatus) {
        return;
      }

      const haystack = [
        student.name,
        student.email,
        metadata.studyName,
        metadata.yearLabel,
        metadata.moduleName,
        metadata.courseName,
        metadata.courseCode
      ]
        .filter(Boolean)
        .join(' ')
        .toLowerCase();

      if (!haystack.includes(searchTerm.trim().toLowerCase())) {
        return;
      }

      const key = `${metadata.studyId}:${student.id}`;
      if (!registrations.has(key)) {
        registrations.set(key, {
          key,
          studyId: metadata.studyId,
          studyName: metadata.studyName,
          studentId: student.id,
          student,
          years: new Map(),
          modules: new Map(),
          courses: new Map(),
          activeCount: 0,
          completedCount: 0,
          cancelledCount: 0,
          latestEnrollmentDate: null
        });
      }

      const registration = registrations.get(key);
      registration.years.set(metadata.yearId, metadata.yearLabel);
      registration.modules.set(metadata.moduleId, metadata.moduleName);
      registration.courses.set(String(course?.id || course?._id || enrollment.courseId), metadata.courseName || course?.name || 'Onbekend vak');

      if (enrollment.status === 'active') registration.activeCount += 1;
      if (enrollment.status === 'completed') registration.completedCount += 1;
      if (enrollment.status === 'cancelled') registration.cancelledCount += 1;

      const enrollmentDate = enrollment.enrollmentDate ? new Date(enrollment.enrollmentDate) : null;
      if (enrollmentDate && !Number.isNaN(enrollmentDate.getTime())) {
        if (!registration.latestEnrollmentDate || enrollmentDate > registration.latestEnrollmentDate) {
          registration.latestEnrollmentDate = enrollmentDate;
        }
      }
    });

    const groupedByStudy = new Map();
    Array.from(registrations.values())
      .sort((left, right) => left.student.name.localeCompare(right.student.name, 'nl'))
      .forEach((registration) => {
        if (!groupedByStudy.has(registration.studyId)) {
          groupedByStudy.set(registration.studyId, {
            studyId: registration.studyId,
            studyName: registration.studyName,
            registrations: []
          });
        }

        groupedByStudy.get(registration.studyId).registrations.push(registration);
      });

    return Array.from(groupedByStudy.values()).sort((left, right) => left.studyName.localeCompare(right.studyName, 'nl'));
  }, [courseMetadataById, data.enrollments, data.students, getCourseById, searchTerm, selectedLocation, selectedStatus, selectedStudy, selectedYear]);

  const availableYears = selectedStudy
    ? courseMetadata.filter((entry) => entry.studyId === selectedStudy)
    : courseMetadata;

  const totalRegistrations = groupedRegistrations.reduce((count, group) => count + group.registrations.length, 0);
  const totalStudents = new Set(
    groupedRegistrations.flatMap((group) => group.registrations.map((registration) => registration.studentId))
  ).size;
  const totalAssignments = groupedRegistrations.reduce(
    (count, group) => count + group.registrations.reduce((sum, registration) => sum + registration.courses.size, 0),
    0
  );

  return (
    <div className="enrollments-page">
      <div className="page-header">
        <div>
          <h1>{t('admin.studyRegistrations')}</h1>
          <p className="page-subtitle">{t('admin.studyRegistrationNote')}</p>
        </div>
      </div>

      <div className="registration-stat-grid">
        <div className="registration-stat-card">
          <span className="registration-stat-value">{groupedRegistrations.length}</span>
          <span className="registration-stat-label">Studies</span>
        </div>
        <div className="registration-stat-card">
          <span className="registration-stat-value">{totalStudents}</span>
          <span className="registration-stat-label">Studenten</span>
        </div>
        <div className="registration-stat-card">
          <span className="registration-stat-value">{totalRegistrations}</span>
          <span className="registration-stat-label">Studie-inschrijvingen</span>
        </div>
        <div className="registration-stat-card">
          <span className="registration-stat-value">{totalAssignments}</span>
          <span className="registration-stat-label">Actieve vakkoppelingen</span>
        </div>
      </div>

      <div className="enrollment-filter-toolbar">
        <input
          type="search"
          className="enrollment-search-input"
          placeholder="Zoek op student, studie, module of vak"
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
        />
        <select className="enrollment-select-input" value={selectedStudy} onChange={(e) => {
          setSelectedStudy(e.target.value);
          setSelectedYear('');
        }}>
          <option value="">Alle studies</option>
          {studiesWithAssignments.map((study) => (
            <option key={study.id} value={study.id}>{getStudyOptionLabel(study)}</option>
          ))}
        </select>
        <select className="enrollment-select-input" value={selectedYear} onChange={(e) => setSelectedYear(e.target.value)}>
          <option value="">Alle studiejaren</option>
          {Array.from(new Map(availableYears.map((entry) => [entry.yearId, entry]))).values().map((entry) => (
            <option key={entry.yearId} value={entry.yearId}>{entry.yearLabel}</option>
          ))}
        </select>
        <select className="enrollment-select-input" value={selectedStatus} onChange={(e) => setSelectedStatus(e.target.value)}>
          <option value="">Alle statussen</option>
          <option value="active">Actief</option>
          <option value="completed">Afgerond</option>
          <option value="cancelled">Geannuleerd</option>
        </select>
      </div>

      <div className="registration-groups">
        {groupedRegistrations.map((studyGroup) => (
          <section key={studyGroup.studyId} className="assignment-section">
            <div className="assignment-section-header">
              <h2>{studyGroup.studyName}</h2>
              <span>{studyGroup.registrations.length} studenten</span>
            </div>

            <div className="registration-grid">
              {studyGroup.registrations.map((registration) => (
                <article key={registration.key} className="registration-card">
                  <div className="registration-card-header">
                    <div>
                      <h3>{registration.student.name}</h3>
                      <p>{registration.student.email}</p>
                    </div>
                    <span className="registration-card-chip">{registration.student.location || 'Onbekende locatie'}</span>
                  </div>

                  <div className="registration-card-meta">
                    <div>
                      <span className="registration-meta-label">Studiejaren</span>
                      <strong>{Array.from(registration.years.values()).join(', ')}</strong>
                    </div>
                    <div>
                      <span className="registration-meta-label">Modules</span>
                      <strong>{registration.modules.size}</strong>
                    </div>
                    <div>
                      <span className="registration-meta-label">Vakken</span>
                      <strong>{registration.courses.size}</strong>
                    </div>
                  </div>

                  <div className="registration-status-row">
                    <span className="status-badge active">Actief {registration.activeCount}</span>
                    <span className="status-badge completed">Afgerond {registration.completedCount}</span>
                    <span className="status-badge cancelled">Geannuleerd {registration.cancelledCount}</span>
                  </div>

                  <div className="registration-course-list">
                    {Array.from(registration.courses.values()).slice(0, 4).map((courseName) => (
                      <span key={courseName} className="registration-course-chip">{courseName}</span>
                    ))}
                    {registration.courses.size > 4 && (
                      <span className="registration-course-chip muted">+{registration.courses.size - 4} meer</span>
                    )}
                  </div>

                  <p className="registration-last-updated">
                    Laatste koppeling: {registration.latestEnrollmentDate ? registration.latestEnrollmentDate.toLocaleDateString('nl-NL') : '-'}
                  </p>
                </article>
              ))}
            </div>
          </section>
        ))}

        {groupedRegistrations.length === 0 && (
          <p className="no-data">{t('common.noData')}</p>
        )}
      </div>
    </div>
  );
};

export default StudyRegistrations;